import { createFileRoute, Link } from "@tanstack/react-router";
import { SectionLabel } from "@/components/ui-extras";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { PublicOrAppShell } from "@/components/public-or-app-shell";

const LILAC = "var(--caileve-lilac-text)";
const INK = "var(--caileve-ink)";
const COPPER = "var(--caileve-copper-text)";
const DIVIDER = "var(--caileve-soft)";

const FAQ_SECTIONS = [
  {
    title: "Getting Started",
    questions: [
      {
        q: "How do I add my first piece?",
        a: "Tap the Jewelry, Clothing, or Accessories tab then tap the + Add button. Take a photo or choose from your library — Cailève reads the photo automatically and fills in the name, category, color, and material. You only confirm the details before saving. To add several pieces at once, choose multiple photos from your library (up to 5) and Cailève will scan each one in sequence.",
      },
      {
        q: "Can I use Cailève on my phone?",
        a: "Yes. Cailève is a progressive web app — no App Store required. On iPhone, open caileve.app in Safari, tap the Share icon, and select Add to Home Screen. On Android, open caileve.app in Chrome, tap the three-dot menu, and select Add to Home Screen. The app installs instantly and opens full screen like a native app.",
      },
      {
        q: "Is there a limit to how many pieces I can add?",
        a: "The free plan supports up to 20 jewelry items, 20 clothing items, and 7 saved looks. Accessories are unlimited on all plans. Upgrade to Premium or Atelier for unlimited jewelry, clothing, and looks.",
      },
      {
        q: "What is the difference between Free, Premium, and Atelier?",
        a: "Free includes 3 Wardrobe Fit scans and 3 Final Touch scans per month — 6 Eye scans total — plus 1 Style Me look per day. Premium gives you unlimited style judgment: unlimited Wardrobe Fit and Final Touch scans, unlimited Style Me looks, and every intelligence feature. Atelier includes everything in Premium, plus Circle Founder status — first access when Private Circles launches — and priority access to every new Cailève feature as it ships.",
      },
      {
        q: "What does Founding Member mean?",
        a: "Founding Members are the first subscribers to support Cailève. They lock in a permanently discounted rate — Premium at $6.99/month and Atelier at $14.99/month — that never increases as long as their subscription is active. Visit the Plans page to see current pricing.",
      },
      {
        q: "Does Cailève have a dark mode?",
        a: "Yes. Go to Profile, tap App Settings, and toggle Dark Mode. Cailève switches to a warm deep-plum palette designed to look and feel like a private atelier at night. Your preference is saved and applies every time you open the app.",
      },
    ],
  },
  {
    title: "Your Collection",
    questions: [
      {
        q: "Are my photos and collection private?",
        a: "Your collection, looks, and personal data are stored privately and are only accessible to you. When you photograph a piece, the image is sent to Anthropic's API for AI analysis — Cailève uses Anthropic to read the piece and generate verdicts. Anthropic does not use your photos to train their models and deletes them within 30 days. Nothing in your collection is ever visible to other users.",
      },
      {
        q: "Can I add accessories like bags and sunglasses?",
        a: "Yes. The Accessories tab in the bottom navigation holds everything that is not jewelry or clothing — bags, sunglasses, belts, scarves, hats, and more. Accessories can be added to any look you build and are included in styling suggestions.",
      },
      {
        q: "Can I organize my collection by category or color?",
        a: "Yes. Each collection screen has a quiet Filter and Sort strip at the top. Tap Filter to filter by category, color, occasion, and more. Tap the current sort label to change the order — by name, date added, favorites first, and others. You can also search your collection by tapping the search icon in the same strip.",
      },
      {
        q: "How do I mark a piece as a favorite?",
        a: "Tap the heart icon on any jewelry, clothing, or accessory card to favorite it. On the home screen, tap the Most Reached For section header to see all your favorites across jewelry, clothing, and accessories in one place.",
      },
      {
        q: "Can I edit or delete a piece after adding it?",
        a: "Yes. Tap any piece in your collection to open its detail page, then tap the menu in the top corner to edit or delete. You can also press and hold a card to reveal a quick photo customisation option that lets you replace the card's background image.",
      },
      {
        q: "How do I customize what details show on my cards?",
        a: "Tap the settings icon (sliders) on any collection screen — Jewelry, Clothing, or Accessories. You can choose which details appear beneath each item photo, such as category, color, material, or storage location. Your preference is saved per collection type.",
      },
    ],
  },
  {
    title: "Looks and Styling",
    questions: [
      {
        q: "How does Style Me work?",
        a: "Tell Cailève about your moment — occasion, season, time of day, and how you want to feel — and tap Build My Look. Cailève composes a complete outfit from your real collection: jewelry, clothing, accessories, footwear, and bag, all considered together. It accounts for the weather, the occasion register, and your style archetype. If you select a specific piece before generating, that piece becomes the anchor — Style Me builds everything around it, and Try Another keeps it fixed while genuinely varying the supporting pieces. After the look is generated, Cailève also shows What Else You Need — any gaps in your collection for the occasion.",
      },
      {
        q: "How do I save a look?",
        a: "After Style Me builds a look for you, tap Save as Look on the result screen. Your saved looks live in the Lookbook tab in the bottom navigation.",
      },
      {
        q: "What is the Daily Look?",
        a: "Each day, Cailève quietly composes a complete look from your collection — surfacing pieces you may have overlooked, weighted toward what you wear least, adjusted for the season. It appears on your home screen and refreshes daily. Think of it as Cailève tending your wardrobe even when you have not opened the app.",
      },
      {
        q: "What is Rediscover?",
        a: "Rediscover surfaces pieces from your collection that have not been worn or styled recently — not the oldest pieces, but the most stylistically relevant overlooked ones. It appears on your home screen when your collection has enough pieces, and rotates daily. Tap a piece to style it directly from the Rediscover page.",
      },
      {
        q: "What is Eye History?",
        a: "Eye History is a record of every piece you have scanned with Cailève Eye, grouped by shopping trip. You can see the photo, the verdict, and the full reasoning for each scan. Tap any scan to expand the verdict and add the piece to your collection. Eye History is accessible from the Eye History card on your home screen.",
      },
      {
        q: "What is Your Edit?",
        a: "Pieces you have saved from Cailève Eye that you are still thinking about. They appear on your home screen as a rotating slot and have their own page where you can see the original verdict reasoning. Tap This one is mine when you are ready to add a piece to your collection permanently.",
      },
      {
        q: "How do I capture how a look makes me feel?",
        a: "When you save a look, you can select feelings and a mood that describe how wearing it makes you feel. You can also capture feelings directly on any individual piece from its detail page. These emotional connections are the foundation of Cailève's personal style intelligence — the more you share, the more Cailève will understand your style.",
      },
      {
        q: "What is my Style Archetype?",
        a: "Cailève reads your collection and behavior to identify a primary style archetype — one of seven identities that describes how you actually dress, not a trend category. Tap your archetype card on Profile to see your full reading, including which pieces most reflect it and which are worth a second look. Your archetype updates as your collection and choices evolve — it is a living signal, not a fixed label.",
      },
      {
        q: "How do I find or update my Style Archetype?",
        a: "Tap the archetype card at the top of your Profile. If you have not taken the style quiz yet, Cailève will ask five quick questions about how you dress. Already have an archetype and think it has changed? Tap 'This isn't quite you? Retake the profile' under your archetype description, or use 'Tell Cailève who you are becoming' to update it directly without retaking the quiz.",
      },
      {
        q: "What is Gift Intelligence?",
        a: "Gift Intelligence lets you share your style profile with someone shopping for you. From your archetype card in Profile, tap Create Style Profile Link to generate a private, time-limited link. Whoever you share it with gets Eye verdicts based on who you are — not just what you already own. The suppressed archetype — the territory you are drawn to but have not yet claimed — is the most valuable signal for a gift giver. Links expire automatically after 48 hours and can be revoked at any time.",
      },
      {
        q: "How do I manage or cancel my subscription?",
        a: "Go to Profile and tap Membership. From there, tap Manage subscription to open the Stripe customer portal, where you can cancel, change your plan, or update your payment method at any time. Your subscription remains active until the end of the current billing period.",
      },
      {
        q: "How do I delete my account and data?",
        a: "Go to Profile, tap App Settings, and tap Remove my account and data. Confirm when prompted. All your data — photos, jewelry, clothing, accessories, looks, and style requests — will be permanently deleted.",
      },
    ],
  },
  {
    title: "Cailève Eye",
    questions: [
      {
        q: "What is Cailève Eye?",
        a: "Cailève Eye has two modes. Wardrobe Fit lets you photograph any piece — in a store, on a friend, anywhere — and receive a verdict built from your specific collection. It reads hallmarks, identifies brands, notes price tags, and tells you whether the piece fits, fills a gap, duplicates something you already own, or is worth waiting on. Final Touch anchors a look you are already wearing and evaluates whether a candidate piece belongs with it — a different judgment from whether it belongs in your wardrobe. Both modes name your actual pieces by name and reason from your real collection.",
      },
      {
        q: "How do I use Wardrobe Fit?",
        a: "Tap the Wardrobe Fit card on your home screen. You can select an occasion first — Dinner, Work, Travel, and others — so Cailève tailors the verdict to that specific moment. Or tap 'or scan without an occasion' for a general evaluation. Then photograph the piece. Cailève reads your collection and returns a verdict — whether the piece fits naturally, fills a genuine gap, or duplicates something you already own.",
      },
      {
        q: "How do I use Final Touch?",
        a: "Tap the Final Touch card on your home screen. First, photograph the look you are currently wearing — your outfit, laid out or on you. Cailève anchors to that look. Then photograph the piece you are considering adding. Cailève evaluates whether the candidate piece belongs with your specific look — not just your wardrobe — and returns a verdict with styling guidance.",
      },
      {
        q: "How many scans do I get?",
        a: "Free accounts include 3 Wardrobe Fit scans and 3 Final Touch scans per month — 6 Eye scans total. Both quotas reset at the start of each month independently. Premium and Atelier members scan without limits on either mode.",
      },
      {
        q: "Can I save a piece I scan?",
        a: "Yes. After a verdict, tap Save to Your Edit and it will appear in Your Edit — with its photo and verdict — until you decide whether to add it permanently to your collection.",
      },
      {
        q: "What is Your Edit?",
        a: "Your Edit is a private shortlist of pieces you are considering but have not yet added to your collection — across jewelry, clothing, and accessories. When you photograph something with Cailève Eye and tap Save to Your Edit, the piece is held there with its photo and verdict until you decide. Find Your Edit on your home screen or from each collection tab.",
      },
      {
        q: "Can I add several items to my collection at once?",
        a: "Yes. Tap + Add on any collection screen, then tap Choose from Library and select up to 5 photos. Cailève scans each one in sequence — reading the name, category, color, and material from each photo. You review and confirm each piece individually before it is saved. This is the fastest way to build your collection from your camera roll.",
      },
      {
        q: "Why does Cailève Eye say my collection is still growing?",
        a: "Cailève Eye becomes more precise as your collection grows. With fewer than 8 pieces, the intelligence is still forming — but that is the beginning, not a limitation. Cailève treats early verdicts as the first chapter of an ongoing conversation. Add a few more pieces and the analysis will deepen with every item you add.",
      },
      {
        q: "Does Cailève remember pieces I have scanned earlier the same day?",
        a: "Yes. Cailève follows your shopping trip across stores and hours, not just a single scan. If you photograph a skirt in one store and a blouse in another an hour later, Cailève already knows about the skirt and tells you how the two work together. This is called The Whole Trip, and no other styling app does this — it is not remembering individual items, it is building your wardrobe in real time, store to store.",
      },
      {
        q: "What is Shopping for someone else?",
        a: "If you are shopping as a gift for someone who uses Cailève, ask them to share their Style Profile Link with you — they can generate one from their Profile under Gift Intelligence. Open the link on your phone and Cailève Eye will evaluate every piece you scan against their identity, not your wardrobe. The verdicts say 'This is her' rather than 'This is you.' The link expires after 48 hours.",
      },
    ],
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ — Cailève" },
      {
        name: "description",
        content:
          "Frequently asked questions about Cailève — how to add jewelry, organize your wardrobe, create looks, and manage your account.",
      },
    ],
  }),
  component: () => (
    <PublicOrAppShell>
      <FaqPage />
    </PublicOrAppShell>
  ),
});

function FaqPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 min-h-screen">
      {/* Hero */}
      <section className="pt-16 pb-12 text-center">
        <SectionLabel>Support</SectionLabel>
        <h1
          className="font-serif-display text-4xl md:text-6xl mt-6 leading-[1.05]"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: INK }}
        >
          Frequently Asked Questions
        </h1>
        <p
          className="font-serif-display italic text-xl mt-5 max-w-xl mx-auto"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: LILAC }}
        >
          Everything you need to know to make the most of your collection.
        </p>
      </section>

      {/* Divider */}
      <div className="flex justify-center mb-12">
        <div className="w-16 h-px" style={{ backgroundColor: COPPER }} />
      </div>

      {/* FAQ Sections */}
      <div className="space-y-14 pb-16">
        {FAQ_SECTIONS.map((section) => (
          <section key={section.title}>
            <h2
              className="text-xs uppercase tracking-[0.25em] mb-6"
              style={{ color: COPPER }}
            >
              {section.title}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {section.questions.map(({ q, a }, idx) => (
                <AccordionItem
                  key={idx}
                  value={`${section.title}-${idx}`}
                  className="border-0"
                  style={{ borderBottom: `1px solid ${DIVIDER}` }}
                >
                  <AccordionTrigger
                    className="py-5 text-base hover:no-underline"
                    style={{
                      color: LILAC,
                      fontFamily: '"Cormorant Garamond", Georgia, serif',
                    }}
                  >
                    {q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="pb-5 pt-0 text-base leading-relaxed"
                    style={{
                      color: INK,
                      fontFamily: '"Cormorant Garamond", Georgia, serif',
                    }}
                  >
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}
      </div>

      {/* Divider */}
      <div className="flex justify-center mb-12">
        <div className="w-16 h-px" style={{ backgroundColor: COPPER }} />
      </div>

      {/* Contact */}
      <section className="pb-16 text-center">
        <h2
          className="font-serif-display text-2xl mb-4"
          style={{ color: INK, fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Still have a question?
        </h2>
        <p
          className="text-lg mb-2"
          style={{
            color: INK,
            fontFamily: '"Cormorant Garamond", Georgia, serif',
          }}
        >
          We would love to help.
        </p>
        <a
          href="mailto:hello@caileve.app"
          className="inline-flex items-center gap-2 text-base font-medium transition-colors hover:opacity-80"
          style={{ color: LILAC }}
        >
          <HelpCircle className="h-4 w-4" strokeWidth={1.5} />
          Reach us at hello@caileve.app
        </a>
      </section>

      {/* Footer links */}
      <section className="pb-12 text-center space-y-3">
        <p className="text-sm">
          <Link
            to="/privacy"
            className="transition-colors hover:opacity-80"
            style={{ color: LILAC }}
          >
            Privacy Policy
          </Link>
          <span className="mx-3" style={{ color: COPPER }}>
            ·
          </span>
          <Link
            to="/terms"
            className="transition-colors hover:opacity-80"
            style={{ color: LILAC }}
          >
            Terms of Service
          </Link>
        </p>
        <p className="text-xs" style={{ color: LILAC }}>
          Copyright 2026 Cailève. All rights reserved.
        </p>
      </section>
    </div>
  );
}
